import DeleteIcon from '@mui/icons-material/Delete';
import { useCallback, useEffect, useState } from 'react';
import editButton from '../../assets/images/editButton.png';
import axios from '../../functions/axios';
import { parseDate } from '../../functions/functions';
import { useAuthContext } from '../../hooks/useAuthContext';
import { DateSelector } from './DateSelector';

import { Autocomplete, Box, Button, CircularProgress, Grid, Paper, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const DataLoading = () => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
      <CircularProgress />
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>Pobieranie danych</Box>
    </Box>
  );
};

function Notes(props) {
  const [apiData, setApiData] = useState(null);
  const [drivers, setDrivers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dayStart, setDayStart] = useState(parseDate().monthStart);
  const [dayEnd, setDayEnd] = useState(parseDate().monthEnd);
  const { user } = useAuthContext();
  const [driverId, setDriverId] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const navigate = useNavigate();

  const getData = useCallback(async () => {
    try {
      const params = {
        startDate: parseDate(dayStart).day,
        endDate: parseDate(dayEnd).day,
        context: 'notes',
      };
      if (driverId) {
        params.driverId = driverId;
      }
      const response = await axios.get('/notes', { params, context: 'notes' });
      setApiData(response.data);
    } catch (err) {
      console.log(err);
    }
  }, [dayEnd, dayStart, driverId]);

  const deleteRecord = useCallback(
    async (record) => {
      const confirmBox = window.confirm('Czy na pewno usunąć ten wpis?');
      if (!confirmBox) return;

      try {
        await axios.delete('/notes', { data: { _id: record._id, context: 'notes' } });
        await wait(500);
        await getData();
      } catch (err) {
        console.log(err);
      }
    },
    [getData],
  );

  useEffect(() => {
    if (!user) return;
    getData();
  }, [getData, user]);

  useEffect(() => {
    if (!user) return;

    (async () => {
      try {
        const response = await axios.get('/driver');
        const transformed = response.data
          .sort((...args) => {
            const [a, b] = args.map(({ name }) => name);
            return a.localeCompare(b, 'pl-PL', { sensitivity: 'base' });
          })
          .map((record) => ({
            label: record.name,
            id: record._id,
          }));
        setDrivers(transformed);
      } catch (err) {
        console.log(err);
      }
    })();
  }, [user]);

  if (!user) {
    return null;
  }

  return (
    <Grid container>
      <Grid item xs={6}>
        <Paper sx={{ height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              margin: '0 5px 15px 0',
              padding: '5px',
              flexWrap: { xs: 'wrap', md: 'wrap', lg: 'nowrap', xl: 'nowrap' },
              overflow: 'visible',
              '@media (max-width:1800px)': {
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 2,
                overflow: 'visible',
              },
            }}
          >
            <DateSelector
              dayStart={dayStart}
              dayEnd={dayEnd}
              setDayStart={setDayStart}
              setDayEnd={setDayEnd}
              searchButtonClick={getData}
              setLoading={setLoading}
            />
            <Autocomplete
              disablePortal
              options={drivers}
              renderInput={(params) => <TextField {...params} label='Kierowca' />}
              onChange={(e, value) => setDriverId(value ? value.id : null)}
              sx={{
                width: { xs: '100%', sm: '100%', md: '100%', lg: 320, xl: 320 },
                minWidth: 220,
                mt: { xs: 1, md: 1, lg: 0, xl: 0 },
                '@media (max-width:1800px)': {
                  ml: '15px',
                },
                '& .MuiInputBase-root': {
                  color: 'white',
                },
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                },
                '& .MuiAutocomplete-input': {
                  color: 'white',
                },
                '& .MuiAutocomplete-option': {
                  color: 'white',
                  backgroundColor: '#222',
                  '&[aria-selected="true"]': {
                    color: 'white',
                  },
                  '&.Mui-focused': {
                    color: 'white',
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              }}
            />
          </Box>
          {apiData && apiData.length === 0 && (
            <Box
              style={{
                margin: '30px',
              }}
            >
              Brak danych do wyświetlenia...
            </Box>
          )}
          {(apiData ?? []).map((record) => {
            if (!record) return null;
            return (
              <Paper
                key={record._id}
                elevation={6}
                onClick={() => setSelectedNote(record)}
                sx={{
                  margin: '18px 20px',
                  padding: '18px 22px',
                  border: '1.5px solid #333',
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, #232526 0%, #1a1a1a 100%)',
                  boxShadow: '0 4px 24px 0 rgba(0,0,0,0.25)',
                  cursor: 'pointer',
                  transition: 'transform 0.12s, box-shadow 0.12s',
                  '&:hover': {
                    boxShadow: '0 6px 32px 0 rgba(0,0,0,0.35)',
                    borderColor: '#1976d2',
                  },
                }}
              >
                <Grid container alignItems='center' spacing={2}>
                  <Grid item xs={2} sx={{ color: '#90caf9', fontWeight: 600, fontSize: '1.05rem', letterSpacing: 0.5 }}>
                    {record.date ? new Date(record.date).toLocaleDateString('pl-PL') : null}
                  </Grid>
                  <Grid item xs={3} sx={{ color: '#fff', fontWeight: 500, fontSize: '1.05rem', textShadow: '0 1px 2px #111' }}>
                    {record.driver?.name}
                  </Grid>
                  <Grid item xs={6} sx={{ color: '#e0e0e0', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {record.value.slice(0, 70)}
                    {record.value.length > 70 ? '...' : null}
                  </Grid>
                  <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' }}>
                    <Box
                      component='img'
                      src={editButton}
                      alt='edytuj'
                      sx={{
                        width: '28px',
                        height: '28px',
                        filter: 'brightness(1.2)',
                        cursor: 'pointer',
                        transition: 'filter 0.15s',
                        '&:hover': { filter: 'brightness(1.6)' },
                      }}
                      onClick={navigate.bind(null, `/notes/${record._id}`)}
                    />
                    <DeleteIcon
                      sx={{
                        width: '28px',
                        height: '28px',
                        color: '#f44336',
                        cursor: 'pointer',
                        transition: 'color 0.15s',
                        '&:hover': { color: '#ff7961' },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteRecord(record);
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            );
          })}
        </Paper>
      </Grid>
      <Grid item xs={6}>
        <Paper sx={{ height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '15px' }}>
            <Button variant='contained' color='primary' onClick={navigate.bind(null, '/notes/create')}>
              Nowa notatka
            </Button>
          </Box>
          {selectedNote && (
            <Box
              sx={{
                margin: '22px 28px',
                display: 'flex',
                gap: '22px',
                textAlign: 'left',
                flexDirection: 'column',
                padding: '32px 18px 32px 32px',
                border: '1.5px solid #333',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #232526 0%, #1a1a1a 100%)',
                boxShadow: '0 4px 24px 0 rgba(0,0,0,0.25)',
                minHeight: '180px',
                maxWidth: '98%',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 16,
                  cursor: 'pointer',
                  color: '#aaa',
                  fontWeight: 900,
                  fontSize: '3rem',
                  zIndex: 2,
                  transition: 'color 0.15s',
                  '&:hover': { color: '#f44336' },
                  userSelect: 'none',
                  lineHeight: 1,
                }}
                onClick={() => setSelectedNote(null)}
                aria-label='Zamknij'
                title='Zamknij'
              >
                ×
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px', mb: 1 }}>
                <Box sx={{ fontWeight: 700, color: '#90caf9', fontSize: '1.15rem', letterSpacing: 0.5 }}>
                  {selectedNote.date ? new Date(selectedNote.date).toLocaleDateString('pl-PL') : null}
                </Box>
                <Box sx={{ fontWeight: 600, color: '#fff', fontSize: '1.1rem', textShadow: '0 1px 2px #111' }}>{selectedNote.driver?.name}</Box>
              </Box>
              <Box sx={{ color: '#e0e0e0', fontSize: '1.05rem', lineHeight: 1.7, wordBreak: 'break-word', whiteSpace: 'pre-line' }}>{selectedNote.value}</Box>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}

export default Notes;
